import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';

/**
 * EntityLink - Generic clickable link to entity detail pages
 * Replaces CustomerLink, VehicleLink, TransactionLink, RTOLink, PaymentLink
 *
 * @param {string} type - Entity type ('customer', 'vehicle', 'transaction', 'rto', 'payment', 'user')
 * @param {number} id - Entity ID to link to
 * @param {string} displayText - Display text for the link (optional)
 * @param {ReactNode} children - Custom content to display (overrides displayText)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 *
 * @example
 * <EntityLink type="customer" id={123} displayText="John Doe" />
 * <EntityLink type="vehicle" id={456}>2020 Honda Civic</EntityLink>
 * <EntityLink type="transaction" id={789} className="btn btn-link btn-sm" />
 */
export const EntityLink = ({ type, id, displayText, children, className, style }) => {
  const navigate = useNavigate();

  if (!id || !type) {
    return <span>{children || displayText || '-'}</span>;
  }

  // Map entity type to route
  const getEntityRoute = () => {
    switch (type.toLowerCase()) {
      case 'customer':
        return `/dashboard/customers/${id}`;
      case 'vehicle':
        return `/dashboard/vehicles/${id}`;
      case 'transaction':
        return `/dashboard/transactions/${id}`;
      case 'rto':
      case 'rto-agreement':
        return `/dashboard/rto/${id}`;
      case 'payment':
        return `/dashboard/payments/${id}`;
      case 'user':
        return `/dashboard/users/${id}`;
      default:
        console.warn(`Unknown entity type: ${type}`);
        return '#';
    }
  };

  // Get default display text based on entity type
  const getDefaultDisplayText = () => {
    switch (type.toLowerCase()) {
      case 'customer':
        return `Customer ${id}`;
      case 'vehicle':
        return `Vehicle ${id}`;
      case 'transaction':
        return `Transaction ${id}`;
      case 'rto':
      case 'rto-agreement':
        return `RTO ${id}`;
      case 'payment':
        return `Payment ${id}`;
      case 'user':
        return `User ${id}`;
      default:
        return `${type} ${id}`;
    }
  };

  const content = children || displayText || getDefaultDisplayText();
  const isCustomButton = className && className.includes('btn');

  const defaultStyle = isCustomButton ? {} : {
    textDecoration: 'none',
    color: '#0d6efd',
    border: 'none',
    background: 'none',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    ...style,
  };

  return (
    <Button
      variant={isCustomButton ? null : 'link'}
      className={className || 'p-0 text-start entity-link'}
      style={defaultStyle}
      onClick={(e) => {
        e.stopPropagation();
        navigate(getEntityRoute());
      }}
      onMouseOver={(e) => {
        if (!isCustomButton) e.currentTarget.style.textDecoration = 'underline';
      }}
      onMouseOut={(e) => {
        if (!isCustomButton) e.currentTarget.style.textDecoration = 'none';
      }}
    >
      {content}
    </Button>
  );
};

// Convenience exports for backwards compatibility
export const CustomerLink = (props) => <EntityLink type="customer" {...props} />;
export const VehicleLink = (props) => <EntityLink type="vehicle" {...props} />;
export const TransactionLink = (props) => <EntityLink type="transaction" {...props} />;
export const RTOLink = (props) => <EntityLink type="rto" {...props} />;
export const PaymentLink = (props) => <EntityLink type="payment" {...props} />;
export const UserLink = (props) => <EntityLink type="user" {...props} />;

export default EntityLink;
